import { NativePartNotifier } from "../lib/nativePartNotifier"
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react"

interface DarkModeContextType {
  isDarkMode: boolean
  toggleDarkMode: () => void
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(
  undefined,
)

const isSharedRoutePath = () =>
  window.location.pathname.startsWith("/shared-route/")

export function DarkModeProvider({ children }: { children: ReactNode }) {
  const forceLightMode = isSharedRoutePath()

  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (forceLightMode) return false
    const savedMode = localStorage.getItem("darkMode")
    return savedMode
      ? JSON.parse(savedMode)
      : window.matchMedia("(prefers-color-scheme: dark)").matches
  })

  useEffect(() => {
    if (forceLightMode) return
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches)
      localStorage.setItem("darkMode", JSON.stringify(e.matches))
    }

    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [forceLightMode])

  useEffect(() => {
    const root = window.document.documentElement
    if (isDarkMode) {
      root.classList.add("dark")
      root.classList.add("bp5-dark")
    } else {
      root.classList.remove("dark")
      root.classList.remove("bp5-dark")
    }
    if (!forceLightMode) {
      localStorage.setItem("darkMode", JSON.stringify(isDarkMode))
      NativePartNotifier.setDarkModeForNativePart(isDarkMode)
    }
  }, [isDarkMode, forceLightMode])

  const toggleDarkMode = () => {
    if (forceLightMode) return
    setIsDarkMode((prev: boolean) => !prev)
  }

  return (
    <DarkModeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  )
}

export function useDarkMode() {
  const context = useContext(DarkModeContext)
  if (context === undefined) {
    throw new Error("useDarkMode must be used within a DarkModeProvider")
  }
  return context
}
